import { ActivatedRoute } from '@angular/router'
import {
  getUpdateMobileUserState,
  getUpdateMobileUserTasksState,
} from '../mobile-users.selectors'
import { Store, select } from '@ngrx/store'
import {
  UpdateField,
  SetDefaultData,
  FetchMobileUserRequest,
  UpdateMobileUserRequest,
} from '../mobile-users.actions'
import {
  AutoAssignTasksRequest,
  UpdateField as TasksUpdateField,
  SetDefaultData as TasksSetDefaultData,
} from '@containers/mobile-tasks/mobile-tasks.actions'
import Validator from '@shared/validatorjs/src/validator'
import { Component, OnInit, OnDestroy } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription, Subject } from 'rxjs'
import { Map } from 'immutable'
import { Clipboard } from '@angular/cdk/clipboard'
import { getMobileTaskTypesState } from '@shared/shared.select'
import { FetchMobileTasksRequest } from '@containers/mobile-tasks/mobile-tasks.actions'
import { filtersToDbQuery } from '@shared/shared.utils'
import {
  getAutoAssignTasksState,
  getMobileTasksState,
} from '@containers/mobile-tasks/mobile-tasks.selectors'
import { TableFilterType } from '@shared/shared.types'
import Swal from 'sweetalert2'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-mobile-user-tasks',
  templateUrl: './mobile-user-tasks.component.html',
  styleUrls: ['./mobile-user-tasks.component.css'],
})
export class MobileUserTasksComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  private state$: Subscription
  public taskState: Map<string, any>
  private taskState$: Subscription
  public userTasksState: Map<string, any>
  private userTasksState$: Subscription
  public isLoading: boolean
  public userId: number
  public filters: any[]
  public tasksAreLoading = false
  public columns: object[] = [
    {
      prop: 'objectLabelId',
      name: 'OBJECT_CODE',
      sortable: true,
      filterable: true,
      filterType: TableFilterType.String,
      filterKey: 'objectLabelId.label',
      sortProp: 'objectLabelId.label',
      template: 'code',
    },
    {
      prop: 'objectType',
      name: 'OBJECT_TYPE',
      sortable: true,
      template: 'objectType',
    },
    {
      prop: 'mobileUser',
      name: 'USER',
      sortable: true,
      render: (row: any) => row.get('displayName'),
    },
    {
      prop: 'type',
      name: 'TYPE',
      sortable: true,
      filterable: true,
      filterType: TableFilterType.String,
      template: 'type',
      select$: null, // Will be set in ngOnInit
    },
    {
      width: '162px',
      name: '',
      template: 'actionsColumn',
    },
  ]
  public autoAssignLoading = false
  private autoAssignState$: Subscription
  public shouldReload = false

  constructor(
    private clipboard: Clipboard,
    public store: Store<any>,
    private route: ActivatedRoute,
    public translateService: TranslateService,
  ) {
    super()
  }

  ngOnInit() {
    // Set the select$ property after store is initialized
    (this.columns[3] as any).select$ = this.store.pipe(select(getMobileTaskTypesState))
    
    this.userId = parseInt(this.route.snapshot.paramMap.get('id'), 10)
    this.store.dispatch<any>(new SetDefaultData('updateUser'))
    this.store.dispatch<any>(new TasksSetDefaultData('autoAssignTasks'))

    this.autoAssignState$ = this.store
      .pipe(select(getAutoAssignTasksState))
      .subscribe((state) => {
        this.autoAssignLoading = state.get('isLoading')
        const assignedTaskCount = state.get('assignedTaskCount')

        if (!this.autoAssignLoading && assignedTaskCount !== null) {
          Swal.fire({
            text: `${assignedTaskCount} ${this.translateService.instant(
              assignedTaskCount == 1
                ? 'TASK_ASSIGNED_TO_USER'
                : 'TASKS_ASSIGNED_TO_USER',
            )}`,
            icon: 'info',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Ok',
            width: 300,
          }).then((result) => {
            if (result.isConfirmed) {
            } else if (result.isDenied) {
            }
          })
        }
      })

    this.state$ = this.store
      .pipe(select(getUpdateMobileUserState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
      })

    this.taskState$ = this.store
      .pipe(select(getMobileTasksState))
      .subscribe((state) => {
        this.taskState = state
        this.tasksAreLoading = state.get('isLoading')
        this.filters = state.get('filters').toJS()

        if (
          state.get('shouldReload') !== this.shouldReload &&
          state.get('shouldReload')
        ) {
          this.handleFilterChange()
        }
        this.shouldReload = state.get('shouldReload')
      })

    this.userTasksState$ = this.store
      .pipe(select(getUpdateMobileUserTasksState))
      .subscribe((state) => {
        this.userTasksState = state
      })

    this.store.dispatch<any>(
      new FetchMobileUserRequest(this.userId, 'updateUser'),
    )

    this.handleFilterChange()
  }

  autoAssign() {
    this.store.dispatch<any>(new AutoAssignTasksRequest(this.userId))
  }

  handlePageChange(page: any) {
    const pagination = (this.taskState.get('pagination') as any).toJS()
    const { page_size, sort, order_by } = pagination

    this.handleFilterChange({ pagination: { page, page_size, sort, order_by } })
  }

  handleFilterChange(data?: any) {
    const filters = data?.filters ?? this.filters
    const pagination =
      data?.pagination ?? (this.taskState.get('pagination') as any).toJS()
    const { page, page_size, sort, order_by } = pagination

    this.store.dispatch(new TasksUpdateField('filters', filters, 'list'))
    this.store.dispatch(new TasksUpdateField('pagination', pagination, 'list'))

    this.store.dispatch<any>(
      new FetchMobileTasksRequest({
        filters: {
          // matchOr: [{ field: 'mobileUser.id', value: this.userId }],
          ...filtersToDbQuery([
            ...filters,
            {
              field: 'mobileUser.id',
              operator: 'and',
              matching: 'IS',
              value: this.userId,
              type: 'string',
            },
          ]),
        },
        pageSize: parseInt(page_size, 10),
        page: parseInt(page, 10) - 1,
        sort: {
          order: sort?.toUpperCase(),
          prop: order_by,
        },
      }),
    )
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.taskState$.unsubscribe()
    this.userTasksState$.unsubscribe()
    this.autoAssignState$.unsubscribe()
  }
}
